import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LuLock,
  LuCreditCard,
  LuUsers,
  LuCalendarDays,
  LuShieldCheck,
  LuScale,
  LuFileText,
  LuPlus,
  LuMinus,
  LuLoader,
  LuBadgePercent,
  LuTag,
} from 'react-icons/lu';
import Icon from '../../components/common/Icon';
import { subscriptionApi, PricingInfo, DurationDiscount } from '../../api/subscription-api';
import { useTheme } from '../../context/ThemeContext';

const MONTH_OPTIONS = [1, 2, 3, 6, 12];

function getDiscount(discounts: DurationDiscount[], months: number): number {
  return discounts.find((d) => d.months === months)?.discount_percent ?? 0;
}

function calcTotal(
  pricePerSeatPerMonth: number,
  seats: number,
  months: number,
  discountPct: number
): number {
  const base = pricePerSeatPerMonth * seats * months;
  return Math.round(base * (1 - discountPct / 100));
}

function formatMwk(amount: number): string {
  return 'MK ' + amount.toLocaleString('en-MW');
}

const FEATURES = [
  { icon: LuScale, label: 'Court case management' },
  { icon: LuFileText, label: 'Document signing' },
  { icon: LuCalendarDays, label: 'Appointments & tasks' },
  { icon: LuUsers, label: 'Team collaboration' },
];

export const SubscriptionRequiredPage: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [seats, setSeats] = useState(1);
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: pricingRes, isLoading: pricingLoading } = useQuery({
    queryKey: ['subscription-pricing-info'],
    queryFn: () => subscriptionApi.getPricingInfo(),
    retry: 2,
  });

  const pricing: PricingInfo | null = pricingRes?.data ?? null;
  const discountPct = pricing ? getDiscount(pricing.discounts, months) : 0;
  const total = pricing
    ? calcTotal(pricing.price_per_seat_per_month, seats, months, discountPct)
    : 0;
  const baseTotal = pricing ? pricing.price_per_seat_per_month * seats * months : 0;
  const savings = baseTotal - total;

  const handlePay = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await subscriptionApi.initiate(seats, months);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Could not get payment URL. Please try again.');
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? 'Payment initiation failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [seats, months]);

  return (
    <div className={isDark ? 'dark' : ''}>
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-[#0b1120]">

        {/* Topbar */}
        <header className="w-full h-14 flex items-center justify-between px-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 backdrop-blur-sm">
          <img
            src={isDark ? '/clicklawWhite.svg' : '/clickLawBlaq.svg'}
            alt="ClickLaw"
            className="h-7 w-auto"
          />
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            <Icon icon={LuShieldCheck} size={14} className="text-primary dark:text-primary-dark" />
            Secure checkout via Paychangu
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-[960px] grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

            {/* Left — value prop */}
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-dark text-xs font-semibold mb-4">
                  <Icon icon={LuLock} size={12} />
                  Subscription required
                </div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white leading-tight mb-3">
                  Unlock your full<br />legal workspace
                </h1>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  Subscribe to get access to the complete ClickLaw suite — manage cases,
                  sign documents, track tasks, and collaborate with your team.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {FEATURES.map(({ icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-4 py-3"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                      <Icon icon={icon} size={15} className="text-primary dark:text-primary-dark" />
                    </div>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-primary dark:text-primary-dark mb-1">
                  <Icon icon={LuTag} size={14} />
                  Save more with longer plans
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Get up to 20% off when you subscribe for 12 months. Discounts are
                  applied automatically at checkout.
                </p>
              </div>
            </div>

            {/* Right — checkout card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-slate-950/50 overflow-hidden">
              <div className="h-1 w-full bg-gradient-to-r from-primary via-primary-dark to-[#6BBBB1]" />

              <div className="p-7">
                <h2 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-5">
                  Configure your plan
                </h2>

                {pricingLoading && (
                  <div className="flex items-center justify-center py-14">
                    <Icon
                      icon={LuLoader}
                      size={24}
                      className="text-primary dark:text-primary-dark animate-spin"
                    />
                  </div>
                )}

                {!pricingLoading && !pricing && (
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4 text-sm text-amber-700 dark:text-amber-400">
                    Pricing has not been configured yet. Please contact support.
                  </div>
                )}

                {pricing && (
                  <>
                    {/* Seats */}
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                          <Icon icon={LuUsers} size={14} className="text-slate-400" />
                          Seats
                        </label>
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          {formatMwk(pricing.price_per_seat_per_month)} / seat / month
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSeats((s) => Math.max(1, s - 1))}
                          className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:border-primary hover:text-primary dark:hover:border-primary-dark dark:hover:text-primary-dark transition-colors"
                        >
                          <Icon icon={LuMinus} size={14} />
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={500}
                          value={seats}
                          onChange={(e) =>
                            setSeats(Math.max(1, parseInt(e.target.value) || 1))
                          }
                          className="flex-1 text-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 py-2 text-sm font-bold focus:outline-none focus:border-primary dark:focus:border-primary-dark transition-colors"
                        />
                        <button
                          onClick={() => setSeats((s) => Math.min(500, s + 1))}
                          className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:border-primary hover:text-primary dark:hover:border-primary-dark dark:hover:text-primary-dark transition-colors"
                        >
                          <Icon icon={LuPlus} size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                        <Icon icon={LuCalendarDays} size={14} className="text-slate-400" />
                        Duration
                      </label>
                      <div className="grid grid-cols-5 gap-1.5">
                        {MONTH_OPTIONS.map((mo) => {
                          const disc = getDiscount(pricing.discounts, mo);
                          const isSelected = months === mo;
                          return (
                            <button
                              key={mo}
                              onClick={() => setMonths(mo)}
                              className={`flex flex-col items-center justify-center rounded-xl border py-2.5 transition-all ${
                                isSelected
                                  ? 'border-primary bg-primary text-white shadow-md shadow-primary/25 dark:shadow-primary/20'
                                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-primary/40 dark:hover:border-primary-dark/40'
                              }`}
                            >
                              <span className="text-xs font-bold">{mo}mo</span>
                              {disc > 0 ? (
                                <span
                                  className={`mt-0.5 text-[10px] font-bold ${
                                    isSelected
                                      ? 'text-primary-light'
                                      : 'text-emerald-500 dark:text-emerald-400'
                                  }`}
                                >
                                  -{disc}%
                                </span>
                              ) : (
                                <span className="mt-0.5 text-[10px] opacity-0">·</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/60 p-4 mb-5 space-y-2 text-sm">
                      <div className="flex justify-between text-slate-500 dark:text-slate-400">
                        <span>
                          {seats} seat{seats !== 1 ? 's' : ''} × {months} month
                          {months !== 1 ? 's' : ''}
                        </span>
                        <span>{formatMwk(baseTotal)}</span>
                      </div>
                      {discountPct > 0 && (
                        <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-medium">
                          <span className="flex items-center gap-1">
                            <Icon icon={LuBadgePercent} size={13} />
                            {discountPct}% multi-month discount
                          </span>
                          <span>−{formatMwk(savings)}</span>
                        </div>
                      )}
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-2.5 flex justify-between font-bold text-slate-800 dark:text-slate-100">
                        <span>Total due today</span>
                        <span className="text-primary dark:text-primary-dark text-base">
                          {formatMwk(total)}
                        </span>
                      </div>
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-xs text-red-600 dark:text-red-400">
                        {error}
                      </div>
                    )}

                    {/* CTA */}
                    <button
                      onClick={handlePay}
                      disabled={loading || total === 0}
                      className="w-full rounded-2xl bg-primary hover:bg-primary-ink active:scale-[0.98] text-white font-semibold py-3.5 text-sm transition-all shadow-lg shadow-primary/25 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Icon icon={LuLoader} size={16} className="animate-spin" />
                          Redirecting to payment…
                        </>
                      ) : (
                        <>
                          <Icon icon={LuCreditCard} size={16} />
                          Pay {formatMwk(total)}
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-3">
                      You will be redirected to Paychangu's secure checkout to complete payment.
                    </p>
                  </>
                )}
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default SubscriptionRequiredPage;
